/**
 * USE_VARIATION_ITEMS - Items and Template Management Hook
 * 
 * SUMMARY:
 * Manages subtask items, template loading, and all delete operations (items and sections).
 * Handles adding items from templates with proper labor/material/subcontractor parsing.

 * 
 * @param {Object} params - Hook parameters
 * @param {Function} params.getIdsToDelete - From useVariationHierarchy to get descendant IDs
 * @returns {Object} Items state and handlers
 */

import { useState } from 'react';
import { useDispatch } from 'react-redux';
import _ from 'lodash';
import idx from 'idx';
import { updateSubTaskAction } from '../../../../slice/add-task';
import { errorSnackBar, warningSnackBar, successSnackBar } from '../../../../actions/common.action';
import { deleteSubTask as deleteSubTaskAction } from '../../../../actions/project-management.action';
import { getTaskTemplates } from '../../../../actions/template-management.action';
import { materialToForm } from '../../add-task/binders/material';
import { subcontractorToForm, updateSubcontractorSubtask } from '../../add-task/binders/subcontractor';
import { laborToForm } from '../../add-task/binders/labor';
import { updateLaborSubtask } from '../../add-task/binders/labor';
import { updateMaterialSubTask } from '../../add-task/binders/material';

export const useVariationItems = ({
    subTaskArray,
    fields,
    resources,
    projectSummary,
    selectedParent,
    setSelectedParent,
    taskState,
    setTaskState,
    validatePlacement,
    expandParentSection,
    getIdsToDelete
}) => {
    const dispatch = useDispatch();

    // Delete dialog state (for items - managed by SubTask component)
    const [deleteSubTask, setDeleteSubTask] = useState({
        open: false,
        id: [],
        type: 'Item',
        index: '',
        description: '',
    });

    // Section delete state (separate from item delete)
    const [sectionDeleteState, setSectionDeleteState] = useState({
        open: false,
        idsToDelete: [],
        description: '',
    });

    /**
     * Filter and enrich labor data with project-specific information
     * 
     * @param {string} id - Labor ID to lookup
     * @returns {Object} Labor object with project-specific rates or empty object
     */
    const filterLabor = (id) => {
        let laborsList = idx(projectSummary, _ => _.labors) || [];
        let labors = laborsList.find((labor) => labor.id === id);
        let labor = {}
        let allLabors = idx(resources, _ => _.list) || [];
        labor = allLabors.find((labor) => labor.id === id);
        if (!labor) return {};
        labor = _.cloneDeep(labor);
        labor['calloutCost'] = 0;
        labor['averageCost'] = idx(labors, _ => _.averageCost) || labor.cost;
        labor['skillDescription'] = '';
        labor['cost'] = idx(labors, _ => _.cost) || 0;
        return labor || {};
    };

    /**
     * Add a new empty subtask or section
     * 
     * @param {boolean} isSection - Whether adding a section
     * @param {string|null} parentId - Parent ID for nesting
     */
    const handleAddTask = (isSection = false, parentId = null) => {
        // Validate placement
        const validation = validatePlacement(isSection, parentId);
        if (!validation.valid) {
            dispatch(errorSnackBar(validation.message));
            return;
        }

        let _temp = _.cloneDeep(subTaskArray);
        const newItem = {
            id: `temp-${Date.now()}-${Math.random()}`,
            description: '',
            categoryId: '',
            labor: [],
            subcontractor: [],
            material: [],
            is_actual_materialcost: false,
            descriptionError: true,
            descriptionLength: true,
            is_section: isSection,
            parent_subtask_id: parentId,
            order: _temp.filter(i => (i.parent_subtask_id || null) === parentId).length + 1
        };

        _temp.push(newItem);
        dispatch(updateSubTaskAction(_temp));

        // Auto-expand parent section
        expandParentSection(parentId);
    };

    /**
     * Load items from template with proper parsing and validation
     * 
     * @param {Array} items - Template items to load
     * @param {boolean} skipClose - Whether to skip closing dialog
     * @param {Object} data - Additional data (profitMargin, etc.)
     */
    const loadItemFromTemplate = (items = [], skipClose = false, data = {}) => {
        let { profitMargin: pmValue } = data;
        if (pmValue === undefined || pmValue === null || pmValue === '') {
            pmValue = (fields.profitMargin.value !== '' && fields.profitMargin.value !== undefined)
                ? fields.profitMargin.value
                : (idx(projectSummary, _ => _.project.projectMarginValue) || 0);
        }

        let _temp = _.cloneDeep(subTaskArray);

        // Defensive: if root is disallowed for items but selectedParent is null, block
        const rootValidation = validatePlacement(false, null);
        if (selectedParent == null && !rootValidation.valid) {
            dispatch(errorSnackBar('Cannot add items at root when sections exist. Choose a section.'));
            if (!skipClose) handleItemTemplateDialog();
            return;
        }

        // Check for duplicates - check both templateId and description within the same parent
        const { newItems, existingItems } = items.reduce((acc, item) => {
            const exists = _temp.some(existingTask =>
                (existingTask.templateId === item.id &&
                    (existingTask.parent_subtask_id || null) === selectedParent) ||
                (existingTask.description === item.description &&
                    (existingTask.parent_subtask_id || null) === selectedParent)
            );
            if (exists) {
                acc.existingItems.push(item.description);
            } else {
                acc.newItems.push(item);
            }
            return acc;
        }, { newItems: [], existingItems: [] });

        if (existingItems.length > 0) {
            dispatch(warningSnackBar(
                `The following items already exist: ${existingItems.join(', ')}`
            ));
        }

        if (newItems.length === 0) {
            if (!skipClose) handleItemTemplateDialog();
            return;
        }

        // Add new items
        for (const iterator of newItems) {
            const { material = [], labor = [], subcontractor = [], description, selectedQty, id } = iterator;

            // Parse JSON strings for labor and subcontractor if needed
            const parsedLabor = labor.map(l => typeof l === 'string' ? JSON.parse(l) : l);
            const parsedSubcontractor = subcontractor.map(s => typeof s === 'string' ? JSON.parse(s) : s);

            // Process labor with proper filterLabor function to enrich with project-specific rates
            const processedLabor = parsedLabor.map(_labor =>
                laborToForm(_labor, {
                    qty: selectedQty,
                    filterLabor  // Use the actual filterLabor function like add-task does
                })
            ).filter(_labor => !_.isEmpty(_labor));

            const processedSubcontractor = parsedSubcontractor.map(_subcontractor =>
                subcontractorToForm(_subcontractor, {
                    qty: selectedQty,
                    profitMargin: pmValue,
                    profitMarginType: fields.profitMarginType.value
                })
            );

            const processedMaterial = material.map(_material =>
                materialToForm(_material, {
                    qty: selectedQty,
                    profitMargin: pmValue,
                    profitMarginType: fields.profitMarginType.value
                })
            );

            const cloneSubTask = {
                id: `temp-${Date.now()}-${Math.random()}`,
                templateId: id,
                description: description,
                categoryId: iterator.categoryId || '',
                selectedQty: selectedQty,
                templateName: description,
                templateQuantity: selectedQty,
                labor: processedLabor,
                subcontractor: processedSubcontractor,
                material: processedMaterial,
                is_actual_materialcost: false,
                descriptionError: !description,
                descriptionLength: !description,
                isedited: true,
                is_section: false,
                parent_subtask_id: selectedParent == null ? null : selectedParent,
            };
            _temp.push(cloneSubTask);
        }

        dispatch(updateSubTaskAction(_temp));

        // Close dialog unless explicitly skipped
        if (!skipClose) handleItemTemplateDialog();
    };

    /**
     * Refresh template quantities
     * 
     * @param {Array} items - Items to refresh
     */
    const refreshHandler = async (items) => {
        if (!items.length) return;

        const { task: taskData, selectedQty } = items[0];
        const templateId = taskData.templateId;

        let template;
        try {
            template = await dispatch(getTaskTemplates(templateId));
        } catch (err) {
            console.error("Failed to load template", templateId, err);
            return;
        }
        if (!template || !Array.isArray(template.subTasks)) {
            dispatch(warningSnackBar("Unable to load the linked template. Refresh aborted."));
            return;
        }

        const updated = subTaskArray.map((currentSubtask) => {
            const matchingTemplateSubtask = template.subTasks.find(
                templateSubtask => (templateSubtask.id || "") === currentSubtask.templateId
            );

            if (!matchingTemplateSubtask) return currentSubtask;

            const templateMaterials = Array.isArray(matchingTemplateSubtask.material)
                ? matchingTemplateSubtask.material.map((m) => (typeof m === 'string' ? JSON.parse(m) : m))
                : [];
            const templateLabors = Array.isArray(matchingTemplateSubtask.labor)
                ? matchingTemplateSubtask.labor.map((l) => (typeof l === 'string' ? JSON.parse(l) : l))
                : [];
            const templateSubcontractors = Array.isArray(matchingTemplateSubtask.subcontractor)
                ? matchingTemplateSubtask.subcontractor.map((s) => (typeof s === 'string' ? JSON.parse(s) : s))
                : [];

            const updatedMaterials = updateMaterialSubTask(currentSubtask, templateMaterials, { templateQuantity: selectedQty });
            const updatedLabours = updateLaborSubtask(currentSubtask, templateLabors, { templateQuantity: selectedQty });
            const updatedSubcontractors = updateSubcontractorSubtask(
                currentSubtask,
                templateSubcontractors,
                { templateQuantity: selectedQty },
                {
                    profitMargin: fields.profitMargin?.value,
                    profitMarginType: fields.profitMarginType?.value
                }
            );

            return {
                ...currentSubtask,
                material: updatedMaterials,
                labor: updatedLabours,
                subcontractor: updatedSubcontractors,
                templateQuantity: selectedQty
            };
        });

        dispatch(updateSubTaskAction(updated));
    };

    /**
     * Handle subtask/section deletion
     * 
     * @param {Object} params - Delete parameters
     */
    const deleteSubTaskHandler = ({ subtaskIndex = '', subTaskId = [], type = 'Item', currentId = '', description = '' }) => {
        setDeleteSubTask(prev => ({
            ...prev,
            open: !prev.open,
            id: subTaskId,
            type: type,
            index: subtaskIndex,
            currentId: currentId,
            description: description,
        }));
    };

    /**
     * Get delete subtask state
     */
    const getDeleteSubTask = () => deleteSubTask;

    /**
     * Confirm deletion - remove item/section and all children
     */
    const confirmDeleteSubTask = async () => {
        const idsToDelete = deleteSubTask.id || [];

        try {
            // Delete from backend if real IDs
            for (const id of idsToDelete) {
                if (id && !id.toString().startsWith('temp-')) {
                    await dispatch(deleteSubTaskAction(id, true));
                }
            }

            // Remove from Redux array (all descendants) - use type-safe comparison
            const idsToDeleteStrings = idsToDelete.map(id => String(id));
            const updatedArray = subTaskArray.filter(item => !idsToDeleteStrings.includes(String(item.id)));

            dispatch(updateSubTaskAction(updatedArray));

            // Close dialog
            setDeleteSubTask({
                open: false,
                id: [],
                type: 'Item',
                index: '',
                description: '',
            });
        } catch (error) {
            console.error('Error deleting:', error);
        }
    };

    /**
     * Handle item template dialog open/close and set parent context
     * 
     * @param {string|null} parentId - Parent section ID or null for root
     */
    const handleItemTemplateDialog = (parentId = null) => {
        // Store the intended parent (null = root)
        setSelectedParent(parentId == null ? null : parentId);
        setTaskState(prev => ({
            ...prev,
            showItemTemplateDialog: !prev.showItemTemplateDialog,
        }));
    };

    /**
     * Handle section delete button click - opens confirmation dialog
     * 
     * @param {Object} section - The section to delete
     */
    const handleSectionDelete = (section) => {
        // Get all descendant IDs (children, grandchildren, etc.)
        const idsToDelete = getIdsToDelete(section, null, [section.id]);

        const hasChildren = section.children && section.children.length > 0;
        const description = `"${section.description}"${hasChildren ? ' and all nested items' : ''}`;

        setSectionDeleteState({
            open: true,
            idsToDelete: idsToDelete,
            description: description,
        });
    };

    /**
     * Confirm section deletion - removes from Redux state and tracks for backend deletion
     */
    const confirmSectionDelete = () => {
        const { idsToDelete } = sectionDeleteState;

        // Filter out only real IDs (not temp-*) for backend deletion
        const realIdsToDelete = idsToDelete.filter(id => id && !id.toString().startsWith('temp-'));

        // Add real IDs to deleteSubTask state so backend deletes them on save
        if (realIdsToDelete.length > 0) {
            setDeleteSubTask(prev => ({
                ...prev,
                id: [...new Set([...(prev.id || []), ...realIdsToDelete])] // Merge and deduplicate
            }));
        }

        // Remove section and all descendants from Redux state
        const idsToDeleteStrings = idsToDelete.map(id => String(id));
        const updatedArray = subTaskArray.filter(item => !idsToDeleteStrings.includes(String(item.id)));

        dispatch(updateSubTaskAction(updatedArray));

        // Close dialog
        setSectionDeleteState({
            open: false,
            idsToDelete: [],
            description: '',
        });
    };

    /**
     * Cancel section deletion - closes dialog
     */
    const cancelSectionDelete = () => {
        setSectionDeleteState({
            open: false,
            idsToDelete: [],
            description: '',
        });
    };

    /**
     * Helper function to escape regex special characters
     * 
     * @param {string} str - String to escape
     * @returns {string} Escaped string
     */
    const escapeRegex = (str) => {
        return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    };

    /**
     * Get next incremental name to avoid conflicts
     * Handles patterns like "Item Name (1)", "Item Name (2)", etc.
     * 
     * @param {string} description - Base description
     * @param {string|null} targetParentId - Target parent section ID
     * @returns {string} Next available name
     */
    const getNextIncrementalName = (description, targetParentId) => {
        const itemsInTarget = subTaskArray.filter(
            item => (item.parent_subtask_id || null) === targetParentId && !item.is_section
        );

        // Create regex to match "description" or "description (N)"
        const baseRegex = new RegExp(`^${escapeRegex(description)}(?: \\((\\d+)\\))?$`);
        
        const existingNumbers = itemsInTarget
            .map(item => {
                const match = item.description.match(baseRegex);
                if (match) {
                    // match[1] is the number in parentheses, or undefined
                    return match[1] ? parseInt(match[1]) : 0;
                }
                return null;
            })
            .filter(num => num !== null);

        // No conflicts found
        if (existingNumbers.length === 0) {
            return description;
        }

        // Find the highest number and increment
        const maxNumber = Math.max(...existingNumbers);
        return `${description} (${maxNumber + 1})`;
    };

    /**
     * Copy an item to a different section
     * Creates a clone with new temp ID and adjusted name if conflicts exist
     * 
     * @param {string} itemId - ID of item to copy
     * @param {string|null} targetParentId - Target parent section ID
     */
    const handleCopyItem = (itemId, targetParentId) => {
        const _temp = _.cloneDeep(subTaskArray);
        const sourceItem = _temp.find(item => item.id === itemId);

        if (!sourceItem) {
            dispatch(errorSnackBar('Item not found'));
            return;
        }

        // Validate placement using hierarchy rules (items are NOT sections)
        const validation = validatePlacement(false, targetParentId);
        if (!validation.valid) {
            dispatch(errorSnackBar(validation.message));
            return;
        }

        // Generate new name if conflicts exist
        const newDescription = getNextIncrementalName(sourceItem.description, targetParentId);

        // Get order for new item in target section
        const itemsInTarget = _temp.filter(
            item => (item.parent_subtask_id || null) === targetParentId && !item.is_section
        );
        const newOrder = itemsInTarget.length + 1;

        // Clone the item with new ID and updated fields
        const copiedItem = {
            ..._.cloneDeep(sourceItem),
            id: `temp-${Date.now()}-${Math.random()}`,
            description: newDescription,
            parent_subtask_id: targetParentId,
            order: newOrder,
            isedited: true,
            // Deep clone arrays to avoid reference issues
            material: _.cloneDeep(sourceItem.material || []),
            labor: _.cloneDeep(sourceItem.labor || []),
            subcontractor: _.cloneDeep(sourceItem.subcontractor || []),
        };

        _temp.push(copiedItem);
        dispatch(updateSubTaskAction(_temp));

        // Expand parent section if applicable
        if (targetParentId) {
            expandParentSection(targetParentId);
        }

        dispatch(successSnackBar(`Item copied as "${newDescription}"`));
    };

    /**
     * Move an item to a different section
     * Checks for conflicts and prevents move if duplicate exists
     * 
     * @param {string} itemId - ID of item to move
     * @param {string|null} targetParentId - Target parent section ID
     */
    const handleMoveItem = (itemId, targetParentId) => {
        const _temp = _.cloneDeep(subTaskArray);
        const itemIndex = _temp.findIndex(item => item.id === itemId);

        if (itemIndex === -1) {
            dispatch(errorSnackBar('Item not found'));
            return;
        }

        const item = _temp[itemIndex];
        
        // Check if already in target section
        if ((item.parent_subtask_id || null) === targetParentId) {
            dispatch(errorSnackBar('Item is already in this section'));
            return;
        }

        // Validate placement using hierarchy rules (items are NOT sections)
        const validation = validatePlacement(false, targetParentId);
        if (!validation.valid) {
            dispatch(errorSnackBar(validation.message));
            return;
        }

        // Check for conflicts in target section
        const itemsInTarget = _temp.filter(
            (i, idx) => idx !== itemIndex && 
            (i.parent_subtask_id || null) === targetParentId && 
            !i.is_section
        );

        const conflict = itemsInTarget.find(i => i.description === item.description);
        if (conflict) {
            dispatch(errorSnackBar(`An item with the name "${item.description}" already exists in the target section. Cannot move.`));
            return;
        }

        // Update item's parent and order
        const newOrder = itemsInTarget.length + 1;
        _temp[itemIndex] = {
            ...item,
            parent_subtask_id: targetParentId,
            order: newOrder,
            isedited: true,
        };

        dispatch(updateSubTaskAction(_temp));

        // Expand parent section if applicable
        if (targetParentId) {
            expandParentSection(targetParentId);
        }

        dispatch(successSnackBar(`Item moved successfully`));
    };

    return {
        // State
        deleteSubTask,
        setDeleteSubTask,
        sectionDeleteState,
        setSectionDeleteState,

        // Handlers
        handleAddTask,
        loadItemFromTemplate,
        refreshHandler,
        deleteSubTaskHandler,
        getDeleteSubTask,
        confirmDeleteSubTask,
        filterLabor,
        handleItemTemplateDialog,

        // Section delete handlers
        handleSectionDelete,
        confirmSectionDelete,
        cancelSectionDelete,

        // Copy/Move handlers
        handleCopyItem,
        handleMoveItem,
    };
};

